import { memo, useEffect, useRef } from 'react';
import type { PaymentEventDetail, PaymentEventRetryEntry } from './adminPaymentEvents.api';

interface Props {
  open: boolean;
  loading: boolean;
  error: string | null;
  detail: PaymentEventDetail | null;
  onClose: () => void;
}

function fmt(date: string | null | undefined): string {
  if (!date) return '-'; // dash matches the empty-state convention used elsewhere in the dashboard
  const d = new Date(date);
  return isNaN(d.getTime()) ? String(date) : d.toLocaleString();
}

// Pretty-print the payload as 2-space JSON; gracefully degrade for non-serializable inputs
// rather than crashing the modal (which would block ops staff mid-debug).
function formatPayload(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  try {
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  } catch {
    return '[unserializable payload]';
  }
}

const PaymentEventDetailModal = memo(function PaymentEventDetailModal({
  open, loading, error, detail, onClose
}: Props) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  // Focus the close button when the modal opens so keyboard users land somewhere useful
  // and Escape immediately works without an extra Tab.
  useEffect(() => {
    if (open && closeRef.current) closeRef.current.focus();
  }, [open]);

  // Escape-to-close: standard modal expectation; effect cleanup prevents listener leaks
  // when the modal is closed without unmounting the host page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="admin-payment-events-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-event-detail-title"
      onClick={onClose} // click-outside dismiss; the inner stopPropagation on the panel keeps clicks inside from closing
    >
      <div className="admin-payment-events-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-payment-events-modal-header">
          <h2 id="payment-event-detail-title" className="admin-payment-events-modal-title">
            Payment event detail
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="admin-payment-events-button secondary"
            onClick={onClose}
            aria-label="Close payment event detail"
          >Close</button>
        </div>

        {loading && (
          <div className="admin-payment-events-empty" role="status">Loading event detail…</div>
        )}

        {error && !loading && (
          <div className="admin-payment-events-error" role="alert">Error: {error}</div>
        )}

        {!loading && !error && detail && (
          <div className="admin-payment-events-modal-body">
            <dl className="admin-payment-events-meta">
              <dt>Event ID</dt><dd>{detail.eventId || detail.id || '-'}</dd>
              <dt>Provider</dt>
              <dd>
                <span className={`admin-payment-events-badge provider-${detail.provider}`}>{detail.provider}</span>
              </dd>
              <dt>Kind</dt><dd>{detail.kind || '-'}</dd>
              <dt>Timestamp</dt><dd>{fmt(detail.timestamp)}</dd>
              <dt>Status</dt>
              <dd>
                <span className={`admin-payment-events-badge ${detail.processed ? 'processed' : 'unprocessed'}`}>
                  {detail.processed ? 'Processed' : 'Unprocessed'}
                </span>
                {detail.processingStatus && detail.processingStatus !== (detail.processed ? 'success' : 'pending') && (
                  <span className="admin-payment-events-modal-status-extra"> ({detail.processingStatus})</span>
                )}
              </dd>
              <dt>Processed at</dt><dd>{fmt(detail.processedAt)}</dd>
              <dt>Processing attempts</dt><dd>{detail.processingAttempts}</dd>
              <dt>Last processing error</dt>
              <dd>{detail.lastProcessingError || '-'}</dd>
              <dt>Created</dt><dd>{fmt(detail.createdAt)}</dd>
              <dt>Updated</dt><dd>{fmt(detail.updatedAt)}</dd>
            </dl>

            <h3 className="admin-payment-events-modal-section">Payload</h3>
            <pre className="admin-payment-events-modal-json" aria-label="Webhook payload">
              {formatPayload(detail.payload)}
            </pre>

            <h3 className="admin-payment-events-modal-section">Handler result</h3>
            <pre className="admin-payment-events-modal-json" aria-label="Handler result">
              {formatPayload(detail.result)}
            </pre>

            {/* task-19: append-only retry audit timeline. Always render the
                section (even when empty) so admins know the field exists and
                are not left wondering whether the data was simply missing. */}
            <h3 className="admin-payment-events-modal-section">Retry history</h3>
            <RetryHistoryList entries={detail.retryHistory} />
          </div>
        )}
      </div>
    </div>
  );
});

// task-19: standalone presentational component for the retry timeline so the
// detail modal stays readable. Newest entries first matches how staff scan
// audit logs ("what just happened?" → top of the list).
function RetryHistoryList({ entries }: { entries: PaymentEventRetryEntry[] }) {
  if (!entries || entries.length === 0) {
    return <div className="admin-payment-events-empty" role="status">No retries recorded for this event.</div>;
  }
  const ordered = [...entries].sort((a, b) => {
    // Sort newest-first by retriedAt; fall back to original order for invalid dates.
    const ta = new Date(a.retriedAt).getTime();
    const tb = new Date(b.retriedAt).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
    return tb - ta;
  });
  return (
    <ol className="admin-payment-events-retry-history" aria-label="Retry history (newest first)">
      {ordered.map((entry, idx) => {
        const when = entry.retriedAt ? new Date(entry.retriedAt) : null;
        const whenLabel = when && !isNaN(when.getTime()) ? when.toLocaleString() : (entry.retriedAt || '-');
        const actor = entry.retriedBy || { apiKeyMasked: 'unknown' };
        // Compose the actor label without leaking unmasked credentials: prefer
        // the human-readable label when present, then fall back to masked key.
        const actorLabel = actor.apiKeyLabel
          ? `${actor.apiKeyLabel} (${actor.apiKeyMasked})`
          : actor.apiKeyMasked;
        return (
          <li key={`${entry.retriedAt}-${idx}`} className="admin-payment-events-retry-entry">
            <div className="admin-payment-events-retry-row">
              <span className={`admin-payment-events-badge ${entry.outcome === 'success' ? 'processed' : 'unprocessed'}`}>
                {entry.outcome === 'success' ? 'Success' : 'Failed'}
              </span>
              <span className="admin-payment-events-retry-when">{whenLabel}</span>
              <span className="admin-payment-events-retry-actor">by {actorLabel}</span>
              {actor.tenantId && <span className="admin-payment-events-retry-tenant">tenant {actor.tenantId}</span>}
            </div>
            {entry.outcome === 'failed' && entry.errorMessage && (
              <div className="admin-payment-events-retry-error" role="note">{entry.errorMessage}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default PaymentEventDetailModal;
